import { Injectable } from '@angular/core';
import * as pdfjsLib from 'pdfjs-dist';

// Configure the PDF.js worker
pdfjsLib.GlobalWorkerOptions.workerSrc = `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js`;

/** pdf.js loading options a caller may set; a different set of options is a different cache entry. */
export interface PdfLoadOptions {
  /** Range requests only: no streaming of the rest of the file */
  disableStream?: boolean;
  /** Fetch only the ranges pages ask for, not the whole file in the background */
  disableAutoFetch?: boolean;
}

/**
 * One page of a large set PDF without downloading the file: range requests for only the bytes that page needs. The
 * sheet viewer's text search and the set review's sharp zoom both open the set PDF this way.
 */
export const PDF_PAGE_RANGES: PdfLoadOptions = { disableStream: true, disableAutoFetch: true };

export interface PdfPageInfo {
  width: number;
  height: number;
  pageNumber: number;
}

@Injectable({ providedIn: 'root' })
export class PdfRenderService {
  private documentCache = new Map<string, pdfjsLib.PDFDocumentProxy>();

  async loadDocument(url: string, options?: PdfLoadOptions): Promise<pdfjsLib.PDFDocumentProxy> {
    const key = options ? `${url}#${JSON.stringify(options)}` : url;
    const cached = this.documentCache.get(key);
    if (cached) return cached;

    const doc = await this.openDocument(url, options).promise;
    this.documentCache.set(key, doc);
    return doc;
  }

  /**
   * A document outside the cache, owned by the caller: `destroy()` the task when done with it, which cancels a load
   * still running and frees the worker's copy of the file (a whole-file buffer in range mode).
   */
  openDocument(url: string, options?: PdfLoadOptions): pdfjsLib.PDFDocumentLoadingTask {
    return pdfjsLib.getDocument({
      url,
      cMapUrl: 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/cmaps/',
      cMapPacked: true,
      ...options,
    });
  }

  async getPageCount(url: string): Promise<number> {
    const doc = await this.loadDocument(url);
    return doc.numPages;
  }

  async getPageInfo(doc: pdfjsLib.PDFDocumentProxy, pageNum: number, scale = 1.5): Promise<PdfPageInfo> {
    const page = await doc.getPage(pageNum);
    const viewport = page.getViewport({ scale });
    return {
      width: viewport.width,
      height: viewport.height,
      pageNumber: pageNum,
    };
  }

  async renderPage(doc: pdfjsLib.PDFDocumentProxy, pageNum: number, scale = 1.5, targetDoc?: Document): Promise<HTMLCanvasElement> {
    const page = await doc.getPage(pageNum);
    const viewport = page.getViewport({ scale });

    const ownerDoc = targetDoc || document;
    const canvas = ownerDoc.createElement('canvas');
    canvas.width = viewport.width;
    canvas.height = viewport.height;

    const ctx = canvas.getContext('2d');
    await page.render({ canvasContext: ctx, viewport }).promise;

    return canvas;
  }

  /**
   * Starts drawing a page onto a new canvas and hands back the render task, so the caller can `cancel()` it (a zoom
   * change, a closed dialog) and release the canvas. `done` settles when the page is drawn; the page's resources are
   * cleaned up either way.
   */
  startRender(page: pdfjsLib.PDFPageProxy, scale: number, targetDoc?: Document): { canvas: HTMLCanvasElement; task: pdfjsLib.RenderTask; done: Promise<HTMLCanvasElement> } {
    const viewport = page.getViewport({ scale });
    const canvas = (targetDoc || document).createElement('canvas');
    canvas.width = Math.floor(viewport.width);
    canvas.height = Math.floor(viewport.height);
    const task = page.render({ canvasContext: canvas.getContext('2d'), viewport });
    const done = task.promise.then(() => canvas).finally(() => page.cleanup());
    return { canvas, task, done };
  }

  /**
   * Like startRender, but only `region` of the page at `scale` (viewport px): the part of a deeply zoomed page in view,
   * which as a whole would pass the browser's canvas limits.
   */
  startRenderRegion(page: pdfjsLib.PDFPageProxy, scale: number, region: { x: number; y: number; width: number; height: number }):
    { canvas: HTMLCanvasElement; task: pdfjsLib.RenderTask; done: Promise<HTMLCanvasElement> } {
    const viewport = page.getViewport({ scale });
    const canvas = document.createElement('canvas');
    canvas.width = Math.max(1, Math.floor(region.width));
    canvas.height = Math.max(1, Math.floor(region.height));
    const task = page.render({ canvasContext: canvas.getContext('2d'), viewport, transform: [1, 0, 0, 1, -region.x, -region.y] });
    const done = task.promise.then(() => canvas).finally(() => page.cleanup());
    return { canvas, task, done };
  }

  async renderPageAsDataUrl(doc: pdfjsLib.PDFDocumentProxy, pageNum: number, scale = 1.5): Promise<string> {
    const canvas = await this.renderPage(doc, pageNum, scale);
    return canvas.toDataURL('image/jpeg', 0.92);
  }

  destroy() {
    this.documentCache.forEach(doc => doc.destroy());
    this.documentCache.clear();
  }
}
